import {
  ReactElement,
  Suspense,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cc from 'classcat';
import { EIconSize } from 'utils/constants/app/ui';
import {
  AVAILABLE_MARKET_STATISTICS,
  EXTERNAL_MARKET_TOOLTIPS,
  Markets_Tooltips
} from 'utils/constants/markets';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { lazy as lazyImport } from 'utils/generic/lazy';
import { isMobile } from 'utils/is-mobile';
import { TEvent } from 'interfaces/sportsbook';
import { Game, Market as TMarket } from 'interfaces/sportsbook-data-levels';
import { HeaderComp } from './Header';
import { EventAction } from 'components/EventAction';
import { FavoriteButtonMarket } from 'components/FavoriteButton';
import { GlobalIcon } from 'components/GlobalIcon';
import { MarketCollapse } from 'components/MarketCollapse';
import { MarketInfoTooltip } from 'components/MarketsList/partials/MarketInfoTooltip';
import { MatchCardBaseGroup } from 'components/MatchCardBaseGroup';
import { templatesProps } from 'newcomponents/Shared/sportsbook/MarketStatistics/templatesMapping';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { Tooltip } from 'newcomponents/UI/Tooltip';
import { getFavoriteActiveStateById } from 'store/selectors/fav-data';
import { CombinCount } from '../style';
import './index.less';

if (SpringConfigs.DIRECTION === 'rtl') {
  import('./rtl.less');
}

const { MarketStatistics } = lazyImport(
  () =>
    import(
      /* webpackChunkName: "market-statistic" */ 'newcomponents/Shared/sportsbook/MarketStatistics'
    )
);

type TMarketRowProps = {
  group: any;
  Index: number;
  sport?: string;
  gameId?: number;
  currentSport?: string;
  toggleCompact?: (notCompact: boolean) => void;
  isAllCollapsed?: boolean;
  game: Game;
  competitionName: string;
  sportName: string;
};

const HEADER_COMP_REQUIRED_COL_COUNT = 3;

export const MarketRow = ({
  group,
  game,
  Index,
  currentSport,
  sport,
  gameId,
  toggleCompact,
  isAllCollapsed,
  competitionName,
  sportName
}: TMarketRowProps): ReactElement => {
  const { t } = useTranslation();

  const favGroupName = `${group?.markets?.[0]?.type || ''}-${
    group?.markets?.[0]?.name || ''
  }`;

  const isFavorite = useSelector(
    getFavoriteActiveStateById(favGroupName, 'markets')
  );

  const hasMarketToolTipBySetting =
    !!SpringConfigs?.MARKETS_TOOLTIPS &&
    group.markets[0].type in Markets_Tooltips;

  const hasExternalToolTip = group.markets[0].type in EXTERNAL_MARKET_TOOLTIPS;

  const hasMarketToolTip = hasMarketToolTipBySetting || hasExternalToolTip;

  const hasEarlyPayout = (group.markets as TMarket[])?.some(
    ({ has_early_payout }) => !!has_early_payout
  );

  const TemplateComponent = useMemo(() => {
    const marketType = group.markets[0].type;
    const availavleMarketType = AVAILABLE_MARKET_STATISTICS[marketType];
    const _template = templatesProps[availavleMarketType?.template];

    return _template;
  }, []);

  const [showMarketStat, setShowMarketStat] = useState(false);
  const showMarketStatIcon =
    Object.keys(AVAILABLE_MARKET_STATISTICS).includes(group.markets[0].type) &&
    SpringConfigs.STATISTICS_SWITCHER &&
    game.is_stat_available;

  const handleStatistic = useCallback(() => {
    if (!showMarketStat) {
      toggleCompact?.(true);
    }

    setShowMarketStat(prev => !prev);
  }, []);

  useEffect(() => {
    if (!group.notCompact) {
      setShowMarketStat(false);
    }
  }, [group.notCompact]);

  return (
    <MarketCollapse
      title={
        group?.name &&
        group.name
          .replace(/\b(\w*team 1\w*)\b/gi, game.team1_name)
          .replace(/\b(\w*team 2\w*)\b/gi, game.team2_name)
      }
      Index={Index}
      isFavorite={isFavorite}
      groupKey={group.key}
      market={group.markets}
      notCompact={group.notCompact}
      allCollapsed={isAllCollapsed}
      toggleCompact={toggleCompact}
      extraMarketInfo={
        hasMarketToolTip ? (
          <Suspense
            fallback={
              <Skeleton.Avatar
                className="market-tooltip"
                size={EIconSize._16}
              />
            }
          >
            <MarketInfoTooltip marketType={group.markets[0].type} />
          </Suspense>
        ) : null
      }
      extra={
        <>
          {showMarketStatIcon && (
            <GlobalIcon
              lib="generic"
              name="statistics"
              theme="default"
              style={
                SpringConfigs.IS_RTL
                  ? { marginLeft: '2px' }
                  : { marginRight: '2px' }
              }
              size={EIconSize._18}
              className={cc([
                'marketList__statistics-icon',
                {
                  'marketList__statistics-icon-active': showMarketStat,
                  'marketList__statistics-icon-hover': !isMobile()
                }
              ])}
              onClick={handleStatistic}
            />
          )}
          {hasEarlyPayout && (
            <Tooltip
              title={t('sportsbook.earlyPayoutInfo')}
              trigger={['hover', 'click']}
              arrowPointAtCenter
            >
              <div className="marketEarlyPayoutContainer">
                <EventAction
                  ghost
                  size="md"
                  icon={
                    <GlobalIcon
                      lib="generic"
                      name="earlypayout"
                      theme="default"
                      size={EIconSize._20}
                      color="var(--v3-success-color-light)"
                    />
                  }
                  onClick={(e: SyntheticEvent) => e.stopPropagation()}
                />
              </div>
            </Tooltip>
          )}

          {group.cashout && (
            <Tooltip
              title={t('sportsbook.cashoutInfo')}
              arrowPointAtCenter
              trigger={['hover', 'click']}
            >
              <EventAction
                ghost
                size="md"
                className="cashOut__icon__container"
                icon={
                  <GlobalIcon
                    lib="generic"
                    name="dollar"
                    theme="default"
                    size={EIconSize._20}
                    style={{ margin: '0 2px' }}
                    className="cashOut__icon"
                  />
                }
                onClick={(e: SyntheticEvent) => e.stopPropagation()}
              />
            </Tooltip>
          )}
          {group.expressId !== undefined && (
            <div className="MarketRowCombineInfo-wrapper">
              <Tooltip
                title={t('sportsbook.combineInfo')}
                className="MarketRowCombineInfo"
                arrowPointAtCenter
                trigger={['hover', 'click']}
              >
                <GlobalIcon
                  lib="generic"
                  name="paperclip"
                  theme="default"
                  size={EIconSize._20}
                  className="marketList__paperclip-icon"
                />
                <CombinCount>{group.expressId}</CombinCount>
              </Tooltip>
            </div>
          )}
        </>
      }
      extraFavourite={
        <FavoriteButtonMarket group={group} className="markets__favorite" />
      }
    >
      <HeaderComp
        team1={game.team1_name}
        team2={game.team2_name}
        name={
          (group.colCount === HEADER_COMP_REQUIRED_COL_COUNT &&
            (Object.values(group.markets[0].event || {}) as TEvent[]).find(
              (event: { type_1: string }) => event.type_1 === 'Tie'
            )?.name) ||
          ''
        }
        marketType={group.marketType}
      />
      {showMarketStat && (
        <Suspense fallback={<TemplateComponent.skeleton />}>
          <MarketStatistics
            currentMarket={AVAILABLE_MARKET_STATISTICS[group.markets[0].type]}
            game={game}
            gameId={gameId}
          />
        </Suspense>
      )}
      <MatchCardBaseGroup
        gameId={game.id || gameId}
        groupedMarkets={group.markets}
        isBlocked={game.is_blocked}
        teamsInfo={{
          team1: game.team1_name,
          team2: game.team2_name,
          startAt: game.start_ts
        }}
        sport={sport}
        gameInfo={{ type: game.type, ...game.info }}
        sportName={sportName}
        competitionName={competitionName}
        currentSport={currentSport}
      />
    </MarketCollapse>
  );
};
